import { useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

export interface DwellTimerConfig {
	/**
	 * Whether the dwell timer should be active
	 */
	isEnabled: boolean;
	/**
	 * Whether the component is currently in the viewport
	 */
	isInViewport: boolean;
	/**
	 * Whether the tab has focus
	 */
	tabHasFocus: boolean;
	/**
	 * Threshold time in milliseconds before firing the dwell event
	 */
	thresholdMs: number;
	/**
	 * Callback when dwell starts (component enters viewport)
	 */
	onDwellStart?: () => void;
	/**
	 * Callback when dwell threshold is reached
	 */
	onDwellThreshold?: (dwellTime: number) => void;
	/**
	 * Callback when dwell ends (component leaves viewport)
	 */
	onDwellEnd?: (dwellTime: number) => void;
}

export const useDwellTimer = (config: DwellTimerConfig): void => {
	const {
		isEnabled,
		isInViewport,
		tabHasFocus,
		thresholdMs,
		onDwellStart,
		onDwellThreshold,
		onDwellEnd,
	} = config;

	const [isThresholdReached, setIsThresholdReached] = useState(false);
	const [dwellTimer, setDwellTimer] = useState<NodeJS.Timeout | null>(null);

	const dwellStartTimeRef = useRef<number>();
	const isDwellActiveRef = useRef(false);
	const isInterruptedRef = useRef(false);
	const isThresholdReachedRef = useRef(false);
	const isInViewportRef = useRef(isInViewport);
	const tabHasFocusRef = useRef(tabHasFocus);
	const onDwellEndRef = useRef(onDwellEnd);

	// Keep refs in sync with current values
	isInViewportRef.current = isInViewport;
	tabHasFocusRef.current = tabHasFocus;
	onDwellEndRef.current = onDwellEnd;

	// Reset state when disabled
	useEffect(() => {
		if (!isEnabled) {
			setIsThresholdReached(false);
			isThresholdReachedRef.current = false;
			isDwellActiveRef.current = false;
			isInterruptedRef.current = false;
			dwellStartTimeRef.current = undefined;
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				setDwellTimer(null);
			}
		}
	}, [isEnabled, dwellTimer]);

	// Handle dwell start
	useEffect(() => {
		if (isEnabled && isInViewport && tabHasFocus) {
			// Start dwell if not already active or if interrupted
			if (!isDwellActiveRef.current || isInterruptedRef.current) {
				// Clear any existing timer
				if (dwellTimer) {
					clearTimeout(dwellTimer);
					setDwellTimer(null);
				}

				isDwellActiveRef.current = true;
				isInterruptedRef.current = false;
				dwellStartTimeRef.current = Date.now();

				// Fire dwell start event (always fire for QSR tracking)
				onDwellStart?.();

				// Set fresh threshold timer if threshold hasn't been reached yet
				if (!isThresholdReachedRef.current) {
					const timer = setTimeout(() => {
						setDwellTimer(null);

						// Only fire threshold event if still in viewport, has focus, and not already reached
						if (
							isInViewportRef.current &&
							tabHasFocusRef.current &&
							!isThresholdReachedRef.current
						) {
							const dwellTime = dwellStartTimeRef.current
								? Date.now() - dwellStartTimeRef.current
								: 0;
							isThresholdReachedRef.current = true;
							setIsThresholdReached(true);

							onDwellThreshold?.(dwellTime);
						}
					}, thresholdMs);

					setDwellTimer(timer);
				}
			}
		}
	}, [
		isEnabled,
		isInViewport,
		tabHasFocus,
		thresholdMs,
		onDwellStart,
		onDwellThreshold,
		dwellTimer,
	]);

	// Handle dwell end (leaving viewport or losing tab focus)
	useEffect(() => {
		if (isEnabled && (!isInViewport || !tabHasFocus) && isDwellActiveRef.current) {
			isDwellActiveRef.current = false;
			isInterruptedRef.current = true;

			const dwellTime = dwellStartTimeRef.current ? Date.now() - dwellStartTimeRef.current : 0;

			// Clear timer when leaving viewport or losing focus
			if (dwellTimer) {
				clearTimeout(dwellTimer);
				setDwellTimer(null);
			}

			// Reset threshold state if dwell hasn't been reached yet to allow consecutive sessions
			if (!isThresholdReached) {
				// Reset start time to prepare for next dwell session
				dwellStartTimeRef.current = undefined;
			}

			// Fire dwell end event
			onDwellEnd?.(dwellTime);
		}
	}, [isEnabled, isInViewport, tabHasFocus, onDwellEnd, dwellTimer, isThresholdReached]);

	// Handle page navigation and unmount
	useEffect(() => {
		const handleNavigation = () => {
			if (isDwellActiveRef.current && dwellStartTimeRef.current) {
				const dwellTime = Date.now() - dwellStartTimeRef.current;
				onDwellEndRef.current?.(dwellTime);
				isDwellActiveRef.current = false; // Prevent duplicate firing
			}
		};

		const removeNavigationListener = bind(window, {
			type: 'beforeunload',
			listener: handleNavigation,
		});

		return () => {
			removeNavigationListener();
			if (isDwellActiveRef.current && dwellStartTimeRef.current) {
				const dwellTime = Date.now() - dwellStartTimeRef.current;
				onDwellEndRef.current?.(dwellTime);
			}
		};
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (dwellTimer) {
				clearTimeout(dwellTimer);
			}
		};
	}, [dwellTimer]);
};
